import { Component, OnDestroy, OnInit, ViewChild } from "@angular/core";
import { NgForm } from "@angular/forms";
import { ActivatedRoute, Router } from "@angular/router";
import { faTimesCircle } from "@fortawesome/free-regular-svg-icons";
import { faSpinner } from "@fortawesome/free-solid-svg-icons";
import { union } from "lodash";
import { firstValueFrom, Subscription } from "rxjs";
import { finalize } from "rxjs/operators";
import LocalizationConstants from "src/app/constants/localization-constants";
import RouteNameConstants from "src/app/constants/route-name-constants";
import { EmailSubscription } from "src/app/models/EmailSubscription";
import { PlanningBoardRole } from "src/app/models/PlanningBoardRole";
import { User } from "src/app/models/User";
import { ModalService } from "src/app/services/modal.service";
import { PendingUserService } from "src/app/services/pending-user.service";
import { ReminderService } from "src/app/services/reminder.service";
import { environment } from "src/environments/environment";

import { ConfirmationDialogData } from "../../common/confirmation-dialog/confirmation-dialog-modal.component";
import { Company } from "./../../../models/Company";
import { Department } from "./../../../models/Department";
import { ReminderSubscription } from "./../../../models/ReminderSubscription";
import { UserSubscription } from "./../../../models/UserSubscription";
import { UserVendor } from "./../../../models/UserVendor";
import { AuthService } from "./../../../services/auth.service";
import { CompanyService } from "./../../../services/company.service";
import { SubscriptionService } from "./../../../services/subscription.service";
import { UserService, WIMSServiceUser } from "./../../../services/user.service";

@Component({
  selector: "app-user",
  templateUrl: "./user.component.html",
  styleUrls: ["./user.component.scss"],
  standalone: false,
})
export class UserComponent implements OnInit, OnDestroy {
  readonly routeNameConstants = RouteNameConstants;
  readonly localizationConstants = LocalizationConstants;

  private subscriptions: Subscription[] = [];

  faClose = faTimesCircle;
  faSpinner = faSpinner;
  isLoading: boolean;
  isSaving: boolean;
  isAddingCompany: boolean;
  userId: number;
  pendingUserId: number;
  user: User;
  editUser: true;
  famCodes: string[];
  companies: Company[];
  roles: string[];
  planningBoardRoles: PlanningBoardRole[];
  reminderSubscriptions: ReminderSubscription[];
  userSubscriptions: UserSubscription[];
  vendorList: string[];
  selectedVendor: string;
  isNewUser: boolean;
  pendingUser: boolean;
  currentEmail: string;
  requestedCompany: string;

  @ViewChild("userForm", { static: false })
  userForm: NgForm;
  noCompanies: boolean;
  noFams: boolean;
  userAlreadyExists: boolean;
  selectedCompany: Company;

  constructor(
    public userService: UserService,
    private reminderService: ReminderService,
    private subscriptionService: SubscriptionService,
    private companyService: CompanyService,
    private pendingUsers: PendingUserService,
    private route: ActivatedRoute,
    private authService: AuthService,
    private router: Router,
    private modalService: ModalService,
  ) {}

  ngOnInit() {
    this.userId = +this.route.snapshot.paramMap.get("id");
    this.pendingUserId = +this.route.snapshot.paramMap.get("pendingId");
    this.isNewUser = !this.userId;
    this.pendingUser = !!this.pendingUserId;

    this.isLoading = true;
    this.userService
      .getParams()
      .pipe(finalize(() => (this.isLoading = false)))
      .subscribe((response) => {
        this.roles = response.roles;
        this.planningBoardRoles = response.planningBoardRoles;
        this.userInit();
      });
  }

  userInit() {
    if (!this.isNewUser) {
      this.getUser();
      return;
    }

    if (this.pendingUser) {
      this.getPendingUser();
      return;
    }

    this.user = new User();
    this.setDefaultPbUserRole();
    this.getReminderSubscriptions();
  }

  private setDefaultPbUserRole(): void {
    const noAccessPbRole = this.planningBoardRoles.find(
      (x) => x.pbRole.toLowerCase() === "no access",
    );
    this.user.planningBoardRole = noAccessPbRole;
    this.user.pbUserList = [this.user.planningBoardRole];
  }

  async getPendingUser() {
    this.isLoading = true;
    const pendingUser = await firstValueFrom(this.pendingUsers.get(this.pendingUserId));

    if (!pendingUser.azureObjectId) {
      this.openCreateInAzureDialog(true);
      return;
    }

    this.requestedCompany = pendingUser.companies.find((x) => x.fullName != null)?.fullName;
    pendingUser.companies = [];
    this.user = pendingUser;

    this.currentEmail = this.user.email;
    this.isLoading = false;
    if (!this.user) return;

    this.roles = union(this.roles, [this.user.role]);

    this.getReminderSubscriptions();
    this.getUserSubscriptions();
    this.user.companies.forEach((c) => this.setDepartments(c));

    // * Get Vendor List (only for user group profile)
    this.getVendorList();
    this.setDefaultPbUserRole();
  }

  private openCreateInAzureDialog(navigateToPendingUsersPage: boolean): boolean {
    const resultObservable = this.modalService.openConfirmationModal(
      new ConfirmationDialogData(
        LocalizationConstants.PENDING_USERS_CONFIRM_GOTO_AZURE_DIALOG_TITLE,
        [LocalizationConstants.PENDING_USERS_CONFIRM_GOTO_AZURE_DIALOG_CONTENT],
      ),
    );

    const subscription = resultObservable.subscribe((result) => {
      if (result) {
        // if user confirms, open the Azure User creation form
        window.open(environment.azureNewUserCreationFormUrl);
      }

      if (navigateToPendingUsersPage) {
        // and return to pending users page
        this.router.navigate([RouteNameConstants.ADMIN_USERS_PENDING]);
      }
    });

    this.subscriptions.push(subscription);
    return true;
  }

  async getUser() {
    this.isLoading = true;
    this.user = await firstValueFrom(this.userService.get(this.userId));
    this.currentEmail = this.user.email;
    this.user.planningBoardRole = this.user.pbUserList[0];

    this.isLoading = false;
    if (!this.user) return;
    if (!this.allowEdit()) {
      alert("You are not allowed to edit selected user.");
      this.router.navigate([RouteNameConstants.ROOT]);
    }

    this.roles = union(this.roles, [this.user.role]);

    //* Trigger role change to fetch company/FAM list on init */
    this.onRoleChange();

    this.getReminderSubscriptions();
    this.getUserSubscriptions();
    this.user.companies.forEach((c) => this.setDepartments(c));

    // * Get Vendor List (only for user group profile)
    this.getVendorList();
  }

  get isMissingFromEntra(): boolean {
    return !this.isNewUser && !!this.user && this.user.existsInEntra === false;
  }

  deleteUser(): void {
    const resultObservable = this.modalService.openConfirmationModal(
      new ConfirmationDialogData(LocalizationConstants.USERS_REMOVE_DIALOG_TITLE, [
        LocalizationConstants.USERS_REMOVE_DIALOG_CONTENT,
      ]),
    );

    const subscription = resultObservable.subscribe((result) => {
      if (!result) return;

      this.isSaving = true;
      this.userService
        .delete(this.userId)
        .pipe(finalize(() => (this.isSaving = false)))
        .subscribe(() => this.router.navigate([RouteNameConstants.ADMIN_USERS]));
    });

    this.subscriptions.push(subscription);
  }

  async onRoleChange() {
    this.user.setUserRole();
    if (this.user.isAdmin) {
      this.user.companies = [];
      this.user.famcodes = [];
    } else if (this.user.isCountryGroupProfile) {
      this.user.companies = [];
      this.famCodes = await this.companyService.getFamCodes().toPromise();
    } else if (this.user.isUserGroupProfile) {
      this.user.famcodes = [];
    }

    if (this.isNewUser && this.user.role) {
      this.getUserSubscriptions();
    }
  }

  onPbRoleChange(): void {
    this.user.pbUserList = [this.user.planningBoardRole];
  }

  //#region Validations
  validateFams() {
    if (this.user.famcodes.length > 0) {
      this.noFams = false;
      return true;
    } else {
      this.noFams = true;
      return false;
    }
  }

  validateCompanies() {
    if (this.user.companies.length > 0) {
      this.noCompanies = false;
      return true;
    } else {
      this.noCompanies = true;
      return false;
    }
  }

  async validateEmail() {
    if (!this.user.email) return;
    if (!this.isNewUser && this.user.email === this.currentEmail) return false;

    this.userAlreadyExists = false;
    this.userAlreadyExists = await firstValueFrom(
      this.userService.userExistsInSpirit(this.user.email),
    );
    return this.userAlreadyExists;
  }

  async canSave() {
    this.userForm.form.markAllAsTouched();

    await this.validateEmail();
    if (this.userAlreadyExists) return false;

    if (this.user.isCountryGroupProfile) {
      return this.validateFams();
    }

    if (this.user.isUserGroupProfile) {
      return this.validateCompanies();
    }

    const userExistsInAzure = await firstValueFrom(this.userService.existsInAzure(this.user.email));
    if (!userExistsInAzure) {
      this.openCreateInAzureDialog(false);
      return false;
    }

    return true;
  }

  //#endregion

  async save() {
    // A user missing from Entra cannot be saved: re-adding them to the required Entra
    // groups would fail on the backend. The banner steers the admin to delete instead.
    if (this.isMissingFromEntra) return;
    if (!(await this.canSave())) return;

    this.user.subscriptions = this.userSubscriptions.filter((s) => s.isActive);
    this.user.emailSubscriptions = this.mapReminderSubscriptions();
    this.isSaving = true;

    if (this.isNewUser) {
      this.userService
        .create(this.user)
        .pipe(finalize(() => (this.isSaving = false)))
        .subscribe((response) => {
          const userId = response;
          this.router.navigate([RouteNameConstants.ADMIN_USERS, userId]);
        });
      return;
    }

    this.userService
      .update(this.user, this.userId)
      .pipe(finalize(() => (this.isSaving = false)))
      .subscribe();
  }

  //#region Company/FAM
  async addCompany(company: Company) {
    if (this.user.companies?.find((c) => c.name === company.name)) return;

    this.isAddingCompany = true;
    const c = new Company(company.fullName, company.name);
    await this.setDepartments(c);
    this.user.companies.splice(0, 0, c);
    this.isAddingCompany = false;
    this.getVendorList();
    this.validateCompanies();
  }

  removeCompany(company) {
    this.user.companies = this.user.companies.filter((c) => c.name !== company.name);
  }

  async setDepartments(company: Company) {
    const departmentList = await this.companyService.getDepartments(company.name).toPromise();

    // * If no departments set API returns empty array
    // * Workaround to fix this issue
    company.departments = company.departments.filter((d) => d.name !== null);

    company.departmentList = departmentList;
  }

  addDepartment(company: Company, department: Department) {
    if (!department?.name || company.departments.find((d) => d.name === department.name)) return;

    company.departments.push(department);
  }

  removeDepartment(company: Company, departmentName) {
    company.departments = company.departments.filter((x) => x.name !== departmentName);
  }

  addFam(famCode) {
    if (this.user.famcodes.includes(famCode)) return;

    this.user.famcodes.push(famCode);
    this.validateFams();
  }

  removeFam(famCode) {
    this.user.famcodes = this.user.famcodes.filter((x) => x != famCode);
  }
  //#endregion

  //#region Subscriptions
  async getUserSubscriptions() {
    this.isLoading = true;
    this.userSubscriptions = await this.subscriptionService.getUserSubscriptionList(this.user);
    this.isLoading = false;
  }

  removeSubscription(item: UserSubscription) {
    this.userSubscriptions = this.userSubscriptions.filter((x) => x.name !== item.name);
  }
  //#endregion

  //#region Reminders
  getReminderSubscriptions() {
    this.reminderSubscriptions = this.reminderService.getReminderSubscriptions(this.user);
  }

  mapReminderSubscriptions() {
    return this.reminderSubscriptions
      .filter((r) => r.selected)
      .map((r) => {
        const subscription: EmailSubscription = {
          types: r.key,
          email: r.email,
          userid: this.user.userId,
        };
        return subscription;
      });
  }
  //#endregion

  //#region Vendors
  async getVendorList() {
    if (!this.user.isUserGroupProfile) return;
    if (!this.user.companies || this.user.companies.length == 0) return;

    const companyId = this.user.companies[0]?.name;
    this.vendorList = await this.companyService.getVendorToList(companyId).toPromise();
  }

  addVendor(vendorName: string) {
    if (this.user.userVendors.find((v) => v.vendorName === vendorName)) return;

    this.user.userVendors.push(
      new UserVendor(vendorName, this.user.userId, this.user.userVendors?.length < 1),
    );
  }

  removeVendor(vendorName) {
    this.user.userVendors = this.user.userVendors.filter((v) => v.vendorName !== vendorName);
  }

  setVendorAsDefault(vendor: UserVendor) {
    this.user.userVendors.forEach((v) => (v.isDefault = v.vendorName === vendor.vendorName));
  }
  //#endregion

  allowEdit() {
    if (this.user.isItemLoading) return false;
    if (this.user.email === WIMSServiceUser) return false;
    if (this.authService.user.userId === this.user.userId) return true;

    if (this.user.isAdminGroupProfile) return this.authService.user?.isAdmin;

    return true;
  }

  public compareWithFnPbRoles(a: PlanningBoardRole, b: PlanningBoardRole): boolean {
    return b === null || a.pbRoleId === b.pbRoleId;
  }

  public toggleFieldService(value: boolean): void {
    this.user.isFieldServiceUser = value;

    if (value) {
      this.user.isEquipmentAdministrationUser = true;
    }
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((x) => x.unsubscribe());
  }
}
